import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { KalDialogConfig, KalDialogService, KalSnackbarService } from '@kalidea/kaligraphi';
import { Select } from '@ngxs/store';
import { EMPTY, Observable, of, race } from 'rxjs';
import { catchError, filter, finalize, take, tap } from 'rxjs/operators';

import { BaseComponent } from 'src/app/_core/tools/base.component';
import { SuperAdminDialogComponent } from 'src/app/_user/dialogs/super-admin-dialog/super-admin-dialog.component';
import { CredentialStoreService } from 'src/app/_user/services/credential-store.service';
import {
    CheckIfUserSuperAdminAction,
    LoginAction,
    LogoutAction
} from 'src/app/_user/store/user/user.action';
import { UserState } from 'src/app/_user/store/user/user.state';
import { graphqlErrors } from 'src/app/utils/errors.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { EditPasswordFormValueType } from 'src/app/_shared/components/shared-edit-password/shared-edit-password.component';
import { MutationUpdateMotDePassePremiereConnexionArgs } from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { GraphqlService } from 'src/app/_core/services/graphql.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginComponent extends BaseComponent implements OnInit {

    @Select(UserState.needChoice) needChoice$: Observable<boolean>;
    @Select(UserState.isFirstTimeLogin) isFirstTimeLogin$: Observable<boolean>;

    form: FormGroup;

    passwordEditControl = new FormControl<EditPasswordFormValueType | null>(null);

    error: { [key: string]: string } = {};

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly dialogService: KalDialogService,
                private readonly snackbarService: KalSnackbarService,
                private readonly graphqlService: GraphqlService,
                private readonly credentialStore: CredentialStoreService) {
        super(cdr);
    }

    @HostListener('keydown.f9')
    key(): void {
        const login = this.form.value.login;
        const password = this.form.value.password;

        if (!login || !password) {
            return;
        }

        this.loading = true;

        this.watch$ = this.dispatch$(new CheckIfUserSuperAdminAction({login, password}))
            .pipe(
                tap(() => this.loading = false),
                filter(() => this.store.selectSnapshot(UserState.isSuperAdmin)),
                tap(() => this.openSuperadminDialog()),
                finalize(() => this.loading = false)
            );
    }

    login(): void {
        this.loading = true;

        const credentials = this.form.value;

        // On enregistre les informations de connexion dans le localStorage
        this.credentialStore.credentials = credentials;

        this.watch$ = this.dispatch$(new LoginAction(credentials)).pipe(
            tap(() => this.loading = false),
            catchError((err) => {
                const errorCode = +graphqlErrors(err, 'code');
                const errorMessage = graphqlErrors(err, 'message') as string;
                let valueChanges: Observable<any>;

                // détermination du type d'erreur
                if ([4, 5].includes(errorCode)) {
                    this.error = {login: errorMessage};
                    valueChanges = this.form.get('login').valueChanges;
                } else if (errorCode === 3) {
                    this.error = {password: errorMessage};
                    valueChanges = race(this.form.get('login').valueChanges, this.form.get('password').valueChanges);
                } else if (errorCode === 2) {
                    this.error = {ceName: errorMessage};
                    valueChanges = this.form.get('ceName').valueChanges;
                } else {
                    this.error = {other: errorMessage};
                    valueChanges = this.form.valueChanges;
                }

                // après un changement on retire le message d'erreur #39459
                this.watch$ = valueChanges.pipe(take(1), tap(() => this.error = null));

                this.loading = false;
                this.cdr.markForCheck();
                return EMPTY;
            })
        );
    }

    logout(): void {
        this.watch$ = this.wrapLoader$(this.dispatch$(new LogoutAction()));
    }

    editFirstTimeConnectionPassword(): void {
        this.watch$ = this.wrapLoader$(this.savePassword$()).pipe(
            tap(() => {
                this.snackbarService.open({title: 'Votre nouveau mot de passe a bien été défini. Veuillez vous reconnecter avec celui-ci.'});
                this.logout();
            }),
            catchError(err => {
                this.snackbarService.open({title: 'Erreur lors de la modification du mot de passe.'});
                this.logout();
                return of(null);
            })
        );
    }

    private savePassword$(): Observable<string> {
        return this.graphqlService.mutateOne<'UpdateMotDePassePremiereConnexion', MutationUpdateMotDePassePremiereConnexionArgs>({
            mutation: gql`
                mutation UpdateMotDePassePremiereConnexion($contexte: ContextInfosInput, $password: String, $confirmPassword: String) {
                    UpdateMotDePassePremiereConnexion(contexte: $contexte, password: $password, confirmPassword: $confirmPassword)
                }
            `,
            variables: {
                password: this.passwordEditControl.value.newPassword,
                confirmPassword: this.passwordEditControl.value.confirmPassword
            }
        });
    }

    private openSuperadminDialog(): void {
        const config = new KalDialogConfig({
            title: 'Sélection du CE sur lequel se connecter',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        });
        const dialogRef = this.dialogService.open(SuperAdminDialogComponent, config);

        this.watch$ = dialogRef.afterClosed.pipe(
            filter(result => result && result.selectedCE),
            tap(data => {
                this.form.get('ceName').patchValue(data.selectedCE.NomBase);
            })
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            login: [null, FormValidator.isNotNull],
            password: [null, FormValidator.isNotNull],
            ceName: [null, FormValidator.isNotNull],
            storeCredentials: [false]
        });

        const credentials = this.credentialStore.credentials;
        if (!!credentials) {
            this.form.patchValue(credentials);
        }
    }

}
